import { cloneDeep, get, has, pick, unset } from 'lodash';
import { Stop } from '@commutifi/modes';
import { CombinedModes } from '@commutifi-fe/constants';
import { type Question, QUESTION_TYPES } from '@commutifi-fe/interfaces';
import { SURVEY_INITIALIZATION_STEP, dropOffKidsQId, hasSecondaryRouteAnswer, primaryRoutePath, routeAttribute, secondaryRouteObjectId, subQuestionIdPrefix } from 'constants/onboarding';
import { type IntlMessageKeys, FormattedMessage } from 'locales/index';
import { generateSubQuestionId } from 'utils/helpers/surveys';
import { isValidCombinedMode } from 'utils/modes';
import { baseQuestion } from 'constants/surveys';
export const primaryRouteQId = `${subQuestionIdPrefix}primary-route-id`;
export const secondaryRouteQId = `${subQuestionIdPrefix}secondary-route-id`;
export const secondaryRouteModesQId = `${subQuestionIdPrefix}secondary-route-modes-id`;

/**
 * Follow-up questions are asked in a sub-section right after the question they depend on (ex. 5.1 after 5),
 * so they never interfere with the survey's own sections, which are integers. They are derived from the
 * answer of the question they follow, in that question's own section: the follow-up's answer can be stored
 * at the same reference key (the commute steps refine the route), so a merge of all sections would let the
 * follow-up's answer decide whether the follow-up itself is asked.
 */
export const deriveFollowUpQuestions = (questionsById: Record<string, Question>, answersByStep: AnswersByStep): Question[] => getFollowUpSlots(questionsById, answersByStep).filter(slot => slot.isAsked).map(slot => slot.question);

/**
 * Deletes the answer of every follow-up question that is not asked any more (the commuter changed the
 * answer it depends on), so it does not come back stale if the commuter brings the follow-up back: the
 * steps of a commute are only valid for the home and office they were drawn for.
 */
export const removeAnswersOfUnaskedFollowUps = (questionsById: Record<string, Question>, answersByStep: AnswersByStep): AnswersByStep => getFollowUpSlots(questionsById, answersByStep).filter(slot => !slot.isAsked && has(answersByStep[slot.question.section], getAnswerPath(slot.question))).reduce<AnswersByStep>((prunedAnswersByStep, {
  question
}) => {
  const sectionAnswers = cloneDeep(prunedAnswersByStep[question.section]);
  unset(sectionAnswers, getAnswerPath(question));
  return {
    ...prunedAnswersByStep,
    [question.section]: sectionAnswers
  };
}, answersByStep);

/**
 * Keeps, in each sub-section, only the answers of the follow-up questions currently asked there, so an answer
 * that does not apply never counts in the conditionals, the cumulated answers or the submission, even before
 * removeAnswersOfUnaskedFollowUps has deleted it from the store.
 */
export const keepAnswersOfAskedFollowUps = (answersByStep: AnswersByStep, followUpQuestions: Question[]) => Object.fromEntries(Object.entries(answersByStep).map(([section, sectionAnswers]) => {
  if (section === SURVEY_INITIALIZATION_STEP || Number.isInteger(Number(section))) {
    return [section, sectionAnswers];
  }
  const answerPaths = followUpQuestions.filter(followUp => followUp.section === Number(section)).map(getAnswerPath);
  return [section, pick(sectionAnswers, answerPaths)];
}));
const nextSubSection = (section: number, step: number) => Number((section + step).toFixed(1));
const getAnswerPath = (question: Question): string | string[] => Array.isArray(question.referenceKey) ? question.referenceKey : question.referenceKey || question.id;
const getFirstLegCategory = (route: unknown) => {
  const legs: unknown = get(route, 'legs');
  const category: unknown = Array.isArray(legs) ? get(legs[0], 'category') : undefined;
  return typeof category === 'string' ? category : undefined;
};
const buildRouteStepsSlot = ({
  id,
  route,
  section,
  referenceKey,
  conditionalQuestionId,
  titleMessageId
}: RouteStepsQuestionProps): FollowUpSlot => {
  const firstLegCategory = getFirstLegCategory(route);
  return {
    isAsked: Boolean(firstLegCategory && isValidCombinedMode(firstLegCategory)),
    question: {
      ...baseQuestion,
      // Drive & Public Transit uses the preset drive-then-transit timeline
      type: firstLegCategory === CombinedModes.DriveAndTransit ? QUESTION_TYPES.commuteTimelineDriveAndTransit : QUESTION_TYPES.commuteTimeline,
      order: 1,
      id,
      section,
      // Same as the field name the steps are saved under, so the section form is prefilled with them
      referenceKey,
      conditionalQuestionId,
      content: {
        id: 'complexe-modes',
        questionId: id,
        locale: 'en-US',
        title: <FormattedMessage id={titleMessageId} />
      }
    }
  };
};

/** Every follow-up a question can have, asked or not (an unasked one may still hold an answer to delete). */
const getFollowUpSlots = (questionsById: Record<string, Question>, answersByStep: AnswersByStep): FollowUpSlot[] => Object.values(questionsById).flatMap(question => {
  if (question.type === QUESTION_TYPES.commuteRouter) {
    return getCommuteRouterFollowUpSlots(question, answersByStep);
  }
  if (question.type === QUESTION_TYPES.activitySelector) {
    return [getActivitySelectorFollowUpSlot(question, answersByStep)];
  }
  return [];
});
const getCommuteRouterFollowUpSlots = (commuteRouter: Question, answersByStep: AnswersByStep): FollowUpSlot[] => {
  const routerAnswers = answersByStep[commuteRouter.section];
  const primaryRouteSteps = buildRouteStepsSlot({
    id: primaryRouteQId,
    route: get(routerAnswers, generateSubQuestionId(commuteRouter, primaryRoutePath)),
    section: nextSubSection(commuteRouter.section, 0.1),
    referenceKey: generateSubQuestionId(commuteRouter, primaryRoutePath),
    conditionalQuestionId: commuteRouter.id,
    titleMessageId: 'commuteRoute.timeline.questions.primaryRoute'
  });
  const secondaryRouteReferenceKey = generateSubQuestionId(commuteRouter, secondaryRouteObjectId);
  const isSecondaryRouteAsked = get(routerAnswers, secondaryRouteReferenceKey) === hasSecondaryRouteAnswer;
  const secondaryRouter: FollowUpSlot = {
    isAsked: isSecondaryRouteAsked,
    question: {
      ...baseQuestion,
      type: QUESTION_TYPES.secondaryCommuteRouter,
      order: 2,
      id: secondaryRouteQId,
      referenceKey: secondaryRouteReferenceKey,
      // +0.2: the primary route steps may already use the first sub-section
      section: nextSubSection(commuteRouter.section, 0.2),
      conditionalQuestionId: commuteRouter.id,
      content: {
        id: 'secondary-route',
        questionId: secondaryRouteQId,
        locale: 'en-US',
        title: <FormattedMessage id="commuteRoute.secondaryRoute.questions.modes" />
      }
    }
  };
  const secondaryRouteSteps = buildRouteStepsSlot({
    id: secondaryRouteModesQId,
    route: get(answersByStep[secondaryRouter.question.section], generateSubQuestionId(secondaryRouter.question, routeAttribute)),
    section: nextSubSection(secondaryRouter.question.section, 0.1),
    referenceKey: generateSubQuestionId(secondaryRouter.question, routeAttribute),
    conditionalQuestionId: secondaryRouteQId,
    titleMessageId: 'commuteRoute.timeline.questions.secondaryRoute'
  });
  return [primaryRouteSteps, secondaryRouter,
  // The secondary commute steps are only asked while the secondary commute is
  {
    ...secondaryRouteSteps,
    isAsked: isSecondaryRouteAsked && secondaryRouteSteps.isAsked
  }];
};
const getActivitySelectorFollowUpSlot = (activitySelector: Question, answersByStep: AnswersByStep): FollowUpSlot => {
  const activities: unknown = get(answersByStep[activitySelector.section], activitySelector.id);
  return {
    isAsked: Array.isArray(activities) && activities.includes(Stop.DropOffKids),
    question: {
      ...baseQuestion,
      type: QUESTION_TYPES.activityMapper,
      order: 1,
      id: dropOffKidsQId,
      // +0.3: asked after the secondary commute steps when both follow the same section
      section: nextSubSection(activitySelector.section, 0.3),
      conditionalQuestionId: activitySelector.id,
      content: {
        id: 'activity-mapper',
        questionId: dropOffKidsQId,
        locale: 'en-US'
      }
    }
  };
};
type FollowUpSlot = {
  question: Question;
  isAsked: boolean;
};
type AnswersByStep = Record<string, Record<string, unknown> | undefined>;
type RouteStepsQuestionProps = {
  id: string;
  route: unknown;
  section: number;
  referenceKey: Question['referenceKey'];
  conditionalQuestionId: string;
  titleMessageId: IntlMessageKeys;
};